/**
 * EditorNumero — i limiti e i tasti rapidi della domanda numerica («Quante persone…?»).
 * I tasti rapidi sono scorciatoie: chi compila può sempre scrivere un numero preciso.
 */

import React from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { tokens } from '../../../theme';
import { CampoTesto, interoDa } from './CampoTesto';
import type { BozzaDomanda } from './bozza';

interface Props {
  domanda: BozzaDomanda;
  onChange: (patch: Partial<BozzaDomanda>) => void;
}

export const EditorNumero: React.FC<Props> = ({ domanda, onChange }) => {
  const scorciatoie = domanda.scorciatoie ?? [];
  const aggiornaScorciatoia = (i: number, patch: { valore?: number; etichetta?: string }) => {
    onChange({ scorciatoie: scorciatoie.map((s, x) => (x === i ? { ...s, ...patch } : s)) });
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <CampoTesto
          label="Minimo"
          value={domanda.minimo === undefined ? '' : String(domanda.minimo)}
          onChange={(v) => onChange({ minimo: interoDa(v) })}
          inputProps={{ inputMode: 'numeric' }}
          helperText="Senza, vale 0"
        />
        <CampoTesto
          label="Massimo"
          value={domanda.massimo === undefined ? '' : String(domanda.massimo)}
          onChange={(v) => onChange({ massimo: interoDa(v) })}
          inputProps={{ inputMode: 'numeric' }}
          helperText="Facoltativo"
        />
        <CampoTesto
          label="Esempio nel campo"
          value={domanda.segnaposto}
          onChange={(v) => onChange({ segnaposto: v })}
        />
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', color: tokens.color.ash, fontWeight: 600, mt: 1.5 }}>
        Tasti rapidi
      </Typography>
      <Stack spacing={1} sx={{ mt: 0.75 }}>
        {scorciatoie.map((s, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <CampoTesto
              label="Valore"
              value={Number.isFinite(s.valore) ? String(s.valore) : ''}
              onChange={(v) => aggiornaScorciatoia(i, { valore: interoDa(v) ?? Number.NaN })}
              inputProps={{ inputMode: 'numeric' }}
              sx={{ width: 120, flexShrink: 0 }}
              fullWidth={false}
            />
            <CampoTesto
              label="Testo del tasto (facoltativo)"
              placeholder="Senza, il numero stesso (es. «Solo io» per 1)"
              value={s.etichetta}
              onChange={(v) => aggiornaScorciatoia(i, { etichetta: v })}
            />
            <IconButton
              size="small"
              sx={{ color: tokens.color.danger }}
              onClick={() => onChange({ scorciatoie: scorciatoie.filter((_, x) => x !== i) })}
              aria-label="Rimuovi tasto rapido"
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => onChange({ scorciatoie: [...scorciatoie, { valore: Number.NaN }] })}
          sx={{ alignSelf: 'flex-start' }}
        >
          Aggiungi tasto rapido
        </Button>
      </Stack>
    </Box>
  );
};
